import React, { useState, useEffect } from "react";

const PrepaymentImpactCalculator = () => {
  const [loanAmount, setLoanAmount] = useState(2000000);
  const [interestRate, setInterestRate] = useState(8.5);
  const [loanTenure, setLoanTenure] = useState(20);
  const [tenureType, setTenureType] = useState("years");
  const [prepayment, setPrepayment] = useState(50000);
  const [prepaymentFrequency, setPrepaymentFrequency] = useState("yearly");
  const [result, setResult] = useState({
    emiWithoutPrepay: "0.00",
    emiWithPrepay: "0.00",
    tenureWithoutPrepay: "0",
    tenureWithPrepay: "0",
    interestWithoutPrepay: "0.00",
    interestWithPrepay: "0.00",
    totalSavings: "0.00",
    tenureSaved: "0",
  });

  // Prepayment Impact Calculation Function
  const calculatePrepaymentImpact = () => {
    const P = parseFloat(loanAmount);
    const annual_rate = parseFloat(interestRate) / 100;
    const r = annual_rate / 12;
    let tenure = parseFloat(loanTenure);
    const prepay = parseFloat(prepayment);

    if (isNaN(P) || isNaN(r) || isNaN(tenure) || P <= 0 || tenure <= 0) return;

    const n = tenureType === "years" ? tenure * 12 : tenure;

    // Calculate EMI
    let emi;
    if (r > 0) {
      emi = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    } else {
      emi = P / n;
    }

    // Scenario 1: Without Prepayment
    let totalInterestWithout = (emi * n) - P;
    let tenureWithout = n;

    // Scenario 2: With Prepayment
    let balance = P;
    let monthsPaid = 0;
    let totalInterestWith = 0;
    const prepayMonthly = prepaymentFrequency === "monthly" ? prepay : 
                         prepaymentFrequency === "yearly" ? prepay / 12 : 0;

    for (let i = 1; i <= n && balance > 0; i++) {
      const interestPayment = balance * r;
      const principalPayment = emi - interestPayment;
      
      balance -= principalPayment;
      totalInterestWith += interestPayment;
      monthsPaid++;

      // Apply prepayment
      if (prepaymentFrequency === "monthly" && prepayMonthly > 0) {
        balance -= prepayMonthly;
      } else if (prepaymentFrequency === "yearly" && i % 12 === 0 && prepay > 0) {
        balance -= prepay;
      }

      if (balance < 0) balance = 0;
      if (balance === 0) break;
    }

    const totalSavings = totalInterestWithout - totalInterestWith;
    const tenureSaved = tenureWithout - monthsPaid;

    setResult({
      emiWithoutPrepay: emi.toFixed(2),
      emiWithPrepay: emi.toFixed(2),
      tenureWithoutPrepay: tenureWithout.toString(),
      tenureWithPrepay: monthsPaid.toString(),
      interestWithoutPrepay: totalInterestWithout.toFixed(2),
      interestWithPrepay: totalInterestWith.toFixed(2),
      totalSavings: totalSavings.toFixed(2),
      tenureSaved: tenureSaved.toString(),
    });
  };

  useEffect(() => {
  calculatePrepaymentImpact();
}, [loanAmount, interestRate, loanTenure, tenureType, prepayment, prepaymentFrequency]);


  const tenureLabel = tenureType === "years" ? "Years" : "Months";

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M16 8v8"></path>
              <path d="M12 8v8"></path>
              <path d="M8 8v8"></path>
              <path d="M9 2v2"></path>
              <path d="M15 2v2"></path>
            </svg>
          </div>
          <h1>Loan Prepayment Impact Calculator</h1>
          <p>
            Compare loan scenarios with and without prepayments to see how much you can save on interest and time
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                  <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                  <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                </svg>
                Loan Details
              </h2>

              {/* Loan Amount */}
              <div className="input-group">
                <label>Loan Amount</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={loanAmount}
                    onChange={(e) => setLoanAmount(e.target.value)}
                    className="calc-input"
                    placeholder="2000000"
                  />
                </div>
                <div className="quick-select-group">
                  {[500000, 1000000, 2000000, 5000000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setLoanAmount(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{(amount / 100000).toFixed(0)}L
                    </button>
                  ))}
                </div>
              </div>

              {/* Interest Rate */}
              <div className="input-group">
                <label>Interest Rate (Annual)</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={interestRate}
                    onChange={(e) => setInterestRate(e.target.value)}
                    className="calc-input"
                    placeholder="8.5"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
                <div className="quick-select-group">
                  {[7, 8.5, 10, 12].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setInterestRate(rate)}
                      className="quick-select-btn secondary"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Loan Tenure */}
              <div className="input-group">
                <label>Original Loan Tenure</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={loanTenure}
                    onChange={(e) => setLoanTenure(e.target.value)}
                    className="calc-input"
                    placeholder="20"
                  />
                </div>
                <div className="quick-select-group">
                  {tenureType === "years" 
                    ? [5, 10, 15, 20, 30].map((years) => (
                        <button
                          key={years}
                          onClick={() => setLoanTenure(years)}
                          className="quick-select-btn success"
                        >
                          {years}Y
                        </button>
                      ))
                    : [12, 24, 36, 60].map((months) => (
                        <button
                          key={months}
                          onClick={() => setLoanTenure(months)}
                          className="quick-select-btn success"
                        >
                          {months}M
                        </button>
                      ))
                  }
                </div>
              </div>

              {/* Tenure Type */}
              <div className="input-group">
                <label>Tenure Type</label>
                <div className="quick-select-group">
                  {[
                    { value: "years", label: "Years" },
                    { value: "months", label: "Months" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setTenureType(type.value);
                        setLoanTenure(type.value === "years" ? 20 : 240);
                      }}
                      className={`quick-select-btn ${tenureType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Prepayment Amount */}
              <div className="input-group">
                <label>Prepayment Amount</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={prepayment}
                    onChange={(e) => setPrepayment(e.target.value)}
                    className="calc-input"
                    placeholder="50000"
                  />
                </div>
                <div className="quick-select-group">
                  {[10000, 25000, 50000, 100000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setPrepayment(amount)}
                      className="quick-select-btn secondary"
                    >
                      ₹{(amount / 1000).toFixed(0)}K
                    </button>
                  ))}
                </div>
              </div>

              {/* Prepayment Frequency */}
              <div className="input-group">
                <label>Prepayment Frequency</label>
                <div className="quick-select-group">
                  {[
                    { value: "monthly", label: "Monthly" },
                    { value: "yearly", label: "Yearly" }
                  ].map((freq) => (
                    <button
                      key={freq.value}
                      onClick={() => setPrepaymentFrequency(freq.value)}
                      className={`quick-select-btn ${prepaymentFrequency === freq.value ? 'primary' : 'secondary'}`}
                    >
                      {freq.label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculatePrepaymentImpact} className="calculate-btn">
                Calculate Impact
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Impact Summary
              </h2>

              <div className="result-card">
                {/* Total Savings - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Total Interest Savings</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="m9 12 2 2 4-4"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.totalSavings).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Save {result.tenureSaved} months with prepayments
                  </div>
                </div>

                {/* Interest Without Prepayment */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Interest Without Prepayment</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                        <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                        <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.interestWithoutPrepay).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Over {result.tenureWithoutPrepay} months
                  </div>
                </div>

                {/* Interest With Prepayment */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Interest With Prepayment</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                        <polyline points="16 7 22 7 22 13"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.interestWithPrepay).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Over {result.tenureWithPrepay} months
                  </div>
                </div>

                {/* Tenure Reduction */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Tenure Reduction</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.tenureSaved} Months
                  </div>
                  <div className="result-subtext">
                    Close loan {(parseInt(result.tenureSaved) / 12).toFixed(1)} years earlier
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Savings Breakdown</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Interest Paid (With Prepayment)</span>
                      <span className="breakdown-percentage">
                        {((parseFloat(result.interestWithPrepay) / parseFloat(result.interestWithoutPrepay)) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${(parseFloat(result.interestWithPrepay) / parseFloat(result.interestWithoutPrepay)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Interest Saved</span>
                      <span className="breakdown-percentage">
                        {((parseFloat(result.totalSavings) / parseFloat(result.interestWithoutPrepay)) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${(parseFloat(result.totalSavings) / parseFloat(result.interestWithoutPrepay)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>How Does Loan Prepayment Impact Your Finances?</h3>
              <p>
                Loan prepayment is making extra payments toward your loan principal beyond the regular EMI. This 
                calculator shows you the powerful impact of prepayments by comparing two scenarios: paying only 
                regular EMIs versus making additional prepayments. When you make prepayments, the extra amount 
                goes directly toward reducing your principal balance, which means less interest accumulation over 
                time. This results in significant interest savings and allows you to close your loan much earlier 
                than the original tenure. Even small, regular prepayments can make a substantial difference over 
                the life of your loan, potentially saving lakhs in interest charges.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Prepayment Impact Formula:</h4>
            <div className="formula-display">
              Savings = Interest (Without Prepay) - Interest (With Prepay)
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Prepayment</div>
                <div className="formula-param-value">Extra amount paid toward principal</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Interest Savings</div>
                <div className="formula-param-value">Total interest saved with prepayments</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Tenure Reduction</div>
                <div className="formula-param-value">Months saved by prepaying</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Impact</div>
                <div className="formula-param-value">Faster loan closure + Lower total cost</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What is loan prepayment and how does it work?</h5>
              <p>
                Loan prepayment means paying extra money toward your loan principal beyond the regular EMI. When 
                you make a prepayment, the extra amount directly reduces your outstanding principal balance. Since 
                interest is calculated on the remaining principal, prepayments reduce future interest charges. You 
                can choose between reducing EMI (lower monthly payments for remaining tenure) or reducing tenure 
                (same EMI but close loan faster). Most banks recommend tenure reduction as it saves more interest. 
                For home loans, prepayments are typically free, but some lenders charge prepayment penalties on 
                other loan types, especially during the initial years.
              </p>
            </div>

            <div className="faq-item">
              <h5>When is the best time to make loan prepayments?</h5>
              <p>
                The earlier you prepay, the greater the savings because interest charges are highest in the initial 
                years when principal is maximum. Even small prepayments in the first 5 years of a 20-year loan can 
                save lakhs in interest. However, prepayments are beneficial at any stage. Consider prepaying when 
                you receive bonuses, tax refunds, matured investments, or surplus income. Make prepayments before 
                your EMI due date so they're applied in the same month. Avoid prepaying if your loan has attractive 
                tax benefits and you can earn higher post-tax returns from investments. Check if there are 
                prepayment penalties or lock-in periods before prepaying.
              </p>
            </div>

            <div className="faq-item">
              <h5>Should I prepay my loan or invest the money instead?</h5>
              <p>
                Compare your loan interest rate with expected post-tax investment returns. If your loan charges 
                9% interest and you can earn 12% post-tax from investments, investing is better. However, prepaying 
                provides guaranteed returns (saved interest) with zero risk. Prepay high-interest loans first 
                (personal loans 11-16%, credit cards 36-42%). For low-interest home loans (8-9%) with tax benefits, 
                investing might be better. Consider your risk profile, financial goals, and peace of mind. A balanced 
                approach works: prepay some, invest some, maintain emergency fund. If nearing retirement or job 
                security concerns exist, prioritize loan prepayment for financial freedom.
              </p>
            </div>

            <div className="faq-item">
              <h5>Are there any charges or penalties for prepaying loans?</h5>
              <p>
                Home loans from most banks in India are prepayment-free with no penalties. RBI mandates that floating 
                rate home loans cannot have prepayment penalties. However, fixed-rate home loans may have penalties 
                (typically 2-3% of prepaid amount). Personal loans, car loans, and business loans often have prepayment 
                penalties ranging from 2-5% during the initial years (first 12-24 months). Some banks waive penalties 
                after a certain period. Always check your loan agreement's prepayment clause. Calculate if savings 
                from prepayment exceed the penalty charge. Some lenders allow limited free prepayments annually before 
                charging penalties.
              </p>
            </div>

            <div className="faq-item">
              <h5>How do I make a prepayment and ensure it reduces principal?</h5>
              <p>
                Contact your bank and formally request a prepayment, specifying that it should be applied to the 
                principal (not advance EMI). Most banks offer online prepayment through net banking or mobile apps. 
                Visit the branch with a prepayment request letter if needed. Get written confirmation showing how 
                the prepayment was applied. Request a revised loan statement showing new principal balance, updated 
                EMI or tenure, and revised amortization schedule. Verify that your prepayment wasn't applied to 
                future EMIs. Make prepayments before your EMI due date so they're effective from that month. Keep 
                all prepayment receipts for tax purposes and loan closure documentation.
              </p>
            </div>

            <div className="faq-item">
              <h5>What's better: reducing EMI or reducing tenure after prepayment?</h5>
              <p>
                Reducing tenure (keeping EMI same) saves significantly more interest than reducing EMI. When you 
                reduce tenure, you pay off the loan faster and pay interest on a smaller balance for less time. 
                Reducing EMI gives immediate cash flow relief but you pay interest for the full original tenure. 
                For example, on a ₹50L loan, reducing tenure might save ₹5L more interest than reducing EMI. Choose 
                EMI reduction only if you need monthly cash flow relief or expect income to decrease. For maximum 
                savings and fastest debt freedom, always choose tenure reduction. Some people do a combination: 
                reduce tenure initially, then reduce EMI if financial circumstances change. Always verify your 
                choice with the bank when prepaying.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrepaymentImpactCalculator;